import { ensureFreshToken } from "../ensure-fresh-token/ensure-fresh-token";
import { createIsomorphicFn, createServerOnlyFn } from "@tanstack/react-start";
import {
  getCookie as getServerCookie,
  getCookies as getServerCookies,
} from "@tanstack/react-start/server";
import Cookies from "js-cookie";
import { getEnv } from "../env/env";
import { APIError } from "../../lib/errors";
import { isServer } from "@tanstack/react-query";
import { AuthMode } from "../../models/enums/auth-mode";
import { ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE } from "../../lib/constants";

const API_BASE_URL = getEnv.VITE_API_URL || "http://localhost:3005/api/v1";
const AUTH_MODE = (getEnv.VITE_AUTH_MODE || AuthMode.header) as AuthMode;

let keepAliveAgent: unknown;
if (isServer) {
  const http = await import("http");
  const https = await import("https");

  const agentOptions = {
    keepAlive: true,
    keepAliveMsecs: 1000,
    maxSockets: 10,
    maxFreeSockets: 10,
    timeout: 60000,
    scheduling: "lifo" as const,
  };

  keepAliveAgent = API_BASE_URL.startsWith("https://")
    ? new https.Agent(agentOptions)
    : new http.Agent(agentOptions);
}

const getServerCookiesFn = createServerOnlyFn(() => getServerCookies());

const getCookieFn = createIsomorphicFn()
  .server(() => getServerCookie)
  .client(() => Cookies.get);

async function getCookie(name: string): Promise<string | undefined> {
  return getCookieFn()(name);
}

function removeCookie(name: string): void {
  if (!isServer) {
    Cookies.remove(name);
  }
}

function normalizeHeaders(
  headers: HeadersInit | undefined,
): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    const result: Record<string, string> = {};
    headers.forEach(([key, value]) => {
      result[key] = value;
    });
    return result;
  }
  return { ...headers };
}

async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  signal?: AbortSignal,
  keepAlive = true,
  shouldRedirect = true,
): Promise<ApiResponse> {
  let token: string | undefined;
  let headers: Record<string, string> = {};
  let fetchOptions: RequestInit = {};

  const isFormData = options.body instanceof FormData;

  if (AUTH_MODE === AuthMode.header) {
    token = await getCookie(ACCESS_TOKEN_COOKIE);
    headers = {
      ...(!isFormData && options.body
        ? { "Content-Type": "application/json" }
        : {}),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...normalizeHeaders(options.headers),
    };
    fetchOptions = {
      ...options,
      headers,
      cache: "no-store",
      signal,
      keepalive: keepAlive,
      credentials: "same-origin",
    };
  } else {
    headers = {
      ...(!isFormData && options.body
        ? { "Content-Type": "application/json" }
        : {}),
      ...normalizeHeaders(options.headers),
    };
    // On the server, forward incoming request cookies to the API explicitly
    if (isServer) {
      const cookieMap = await getServerCookiesFn();
      const cookieHeader = Array.from(Object.entries(cookieMap))
        .map(([k, v]) => `${k}=${v}`)
        .join("; ");
      if (cookieHeader) {
        headers = { ...headers, Cookie: cookieHeader };
      }
    }
    fetchOptions = {
      ...options,
      headers,
      cache: "no-store",
      signal,
      keepalive: keepAlive,
      // credentials is only meaningful for same-origin; we already forward cookies on server
      credentials: "include",
    };
  }

  // Add keep-alive agent only on server-side
  if (isServer && keepAliveAgent && keepAlive) {
    // @ts-expect-error - Node.js specific agent property
    fetchOptions.agent = keepAliveAgent;
    fetchOptions.cache = "no-store";
  }

    const response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);

    if (response.status === 401) {
      // Handle token refresh
      let refreshResult;
      if (AUTH_MODE === "header") {
        refreshResult = await refreshTheTokenInHeaders();
      } else {
        refreshResult = await refreshTheTokenWithCookies();
      }
      if (!refreshResult.success) {
        removeCookie(ACCESS_TOKEN_COOKIE);
        removeCookie(REFRESH_TOKEN_COOKIE);
        if (!isServer) {
          if (shouldRedirect) {
            window.location.href = "/login";
          } else {
            throw new APIError("Unauthorized", 401, "Unauthorized");
          }
        }
        throw new APIError("Unauthorized", 401, "Unauthorized");
      }

      // Retry the original request with new token (header mode)
      let retryOptions = { ...fetchOptions };
      if (AUTH_MODE === "header" && refreshResult.access_token) {
        retryOptions = {
          ...fetchOptions,
          headers: {
            ...headers,
            Authorization: `Bearer ${refreshResult.access_token}`,
          },
        };
      } else if (AUTH_MODE !== "header" && isServer) {
        const cookieMap = await getServerCookiesFn();
        const cookieHeader = Array.from(Object.entries(cookieMap))
          .map(([k, v]) => `${k}=${v}`)
          .join("; ");
        retryOptions = {
          ...fetchOptions,
          headers: {
            ...headers,
            ...(cookieHeader ? { Cookie: cookieHeader } : {}),
          },
        };
      }
      const retryResponse = await fetch(`${API_BASE_URL}${url}`, retryOptions);
      let data = {};
      if (retryResponse.status !== 204) {
        try {
          data = await retryResponse.json();
        } catch (error) {
          console.error(error);
        }
      }
      return {
        data,
        status: retryResponse.status,
        statusText: retryResponse.statusText,
        headers: retryResponse.headers,
        config: options,
      };
    }

    if (!response.ok) {
      const body = await response.json();
      throw new APIError(
        body.message || `HTTP error! status: ${response.status}`,
        body.statusCode || response.status,
        body.error || response.statusText,
      );
    }

    let data = {};
    if (response.status !== 204) {
      try {
        data = await response.json();
      } catch (error) {
        console.error(error);
      }
    }
    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: options,
    };
}

async function refreshTheTokenInHeaders(): Promise<{
  access_token?: string;
  refresh_token?: string;
  success: boolean;
}> {
  const success = await ensureFreshToken();
  if (!success)
    return {
      success,
    };
  const accessToken = await getCookie(ACCESS_TOKEN_COOKIE);
  const refreshToken = await getCookie(REFRESH_TOKEN_COOKIE);
  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    success,
  };
}

async function refreshTheTokenWithCookies(): Promise<{
  access_token?: string;
  refresh_token?: string;
  success: boolean;
}> {
  const success = await ensureFreshToken();
  return {
    success,
  };
}

interface MutationConfig<TVariables> {
  url: string;
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  data?: TVariables;
}

export async function mutation<TData, TVariables>({
  url,
  method,
  data,
}: MutationConfig<TVariables>): Promise<TData> {
  const response = await fetchWithAuth(url, {
    method,
    body: data ? JSON.stringify(data) : undefined,
  });
  return response as TData;
}

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: RequestInit;
}

const api = {
  get: <T>(
    url: string,
    options?: RequestInit,
    signal?: AbortSignal,
    keepAlive?: boolean,
    shouldRedirect = true,
  ): Promise<ApiResponse<T>> =>
    fetchWithAuth(url, options, signal, keepAlive, shouldRedirect) as Promise<
      ApiResponse<T>
    >,
  post: <T>(
    url: string,
    data?: unknown,
    options?: RequestInit,
    signal?: AbortSignal,
    keepAlive?: boolean,
  ): Promise<ApiResponse<T>> => {
    let body: BodyInit | null | undefined = undefined;
    if (data instanceof FormData) {
      body = data;
    } else if (typeof data === "string") {
      body = data;
    } else if (data !== undefined && data !== null) {
      body = JSON.stringify(data);
    }
    return fetchWithAuth(
      url,
      {
        method: "POST",
        body,
        ...options,
        signal,
      },
      signal,
      keepAlive,
    ) as Promise<ApiResponse<T>>;
  },
  put: <T>(
    url: string,
    data?: unknown,
    options?: RequestInit,
    signal?: AbortSignal,
    keepAlive?: boolean,
  ): Promise<ApiResponse<T>> => {
    let body: BodyInit | null | undefined = undefined;
    if (data instanceof FormData) {
      body = data;
    } else if (typeof data === "string") {
      body = data;
    } else if (data !== undefined && data !== null) {
      body = JSON.stringify(data);
    }
    return fetchWithAuth(
      url,
      {
        method: "PUT",
        body,
        ...options,
        signal,
      },
      signal,
      keepAlive,
    ) as Promise<ApiResponse<T>>;
  },
  patch: <T>(
    url: string,
    data?: unknown,
    options?: RequestInit,
    signal?: AbortSignal,
    keepAlive?: boolean,
  ): Promise<ApiResponse<T>> => {
    let body: BodyInit | null | undefined = undefined;
    if (data instanceof FormData) {
      body = data;
    } else if (typeof data === "string") {
      body = data;
    } else if (data !== undefined && data !== null) {
      body = JSON.stringify(data);
    }
    return fetchWithAuth(
      url,
      {
        method: "PATCH",
        body,
        ...options,
        signal,
      },
      signal,
      keepAlive,
    ) as Promise<ApiResponse<T>>;
  },
  delete: <T>(
    url: string,
    options?: RequestInit,
    signal?: AbortSignal,
    keepAlive?: boolean,
  ): Promise<ApiResponse<T>> =>
    fetchWithAuth(
      url,
      { method: "DELETE", ...options, signal },
      signal,
      keepAlive,
    ) as Promise<ApiResponse<T>>,
};

export default api;
